import { CircleDollarSign, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useScheduledJobStatus } from '~/features/catalog-operations/hooks/use-scheduled-job-status';
import type { BatchHeader } from '~/features/catalog-operations/types/catalog-operations.types';
import {
  isExperimentSource,
  isFeedAuditSource,
  isTrialSource,
} from '~/features/catalog-operations/utils/batch-flow-icons';
import { useCostActual, useCostEstimate } from '~/features/cost-estimate/hooks/use-cost-estimate';
import { formatUsd } from '~/features/cost-estimate/utils/format-usd';
import { useCostRates } from '~/features/run-wizard/hooks/use-cost-rates';
import { formatRate, runCost } from '~/features/run-wizard/utils/run-cost';
import { useCanSeeCost, useShowCostAmounts } from '~/hooks/useCanSeeCost';
import { useAuthStore } from '~/store/auth.store';
import { cn } from '~/utils/cn';

import {
  costParts,
  plannedByPart,
  runProducts,
  spentUsd,
  type ContentSplit,
} from '../utils/batch-cost-parts';
import { rateConnectorOf, runCostOptionsOf } from '../utils/recomputed-cost';

import { CollapsibleSectionCard } from './collapsible-section-card';

const TEST_ID = 'enrichment-layout-batch-info-cost';

/**
 * Batch Info "Cost" section, for people who may see cost only (lily.ai, cost approvers): customers
 * get nothing and fire no request. It shows what exists, in order: the cost planned at creation
 * (or, for an older batch without one, the same calculation recomputed from today's rates), the
 * gate's estimate after the sample, and the spend recorded so far — e.g. "Planned $0.17 · spent
 * $0.04 of ≈ $0.12". Open, each part's planned amount sits next to its spend.
 */
export function BatchInfoCostSection({ data }: { data: BatchHeader }) {
  const canSeeCost = useCanSeeCost();
  if (!canSeeCost) return null;
  return <CostSectionBody data={data} />;
}

/** Scheduled / manual funnel batches have a job status; trials, experiments and audits 404 there. */
function hasJobStatus(data: BatchHeader): boolean {
  return (
    !isExperimentSource(data.triggerSource) &&
    !isFeedAuditSource(data.triggerSource) &&
    !isTrialSource(data.triggerSource)
  );
}

/**
 * Estimate and spend for the batch, read by batch id (workflow #1054: the full run's estimate,
 * and an actual summed over every run with `runs[]`) — the same keys as the batch page's cost
 * card, so a normal open adds no request. A 204 there is definitive ("nothing yet"). Only an
 * answer without `runs[]` means the batch-id read is not supported (a backend before #1054), and
 * only then are the run ids read: the pipeline, and the sample for its content.
 */
function useBatchCost(data: BatchHeader) {
  const batchCostId = data.workflowId ?? data.batchId;
  const byBatchEstimate = useCostEstimate(batchCostId);
  const byBatchActual = useCostActual(batchCostId);
  const batchActual = byBatchActual.data ?? null;
  const unsupported = batchActual !== null && !Array.isArray(batchActual.runs);
  const { data: jobStatus } = useScheduledJobStatus(
    data.batchId,
    unsupported && hasJobStatus(data),
  );
  const fullRunId = unsupported
    ? (data.pipelineWorkflowId ?? jobStatus?.pipeline?.workflowId ?? null)
    : null;
  const sampleId = unsupported ? (jobStatus?.sampleEnrichment?.workflowId ?? null) : null;
  const byRunEstimate = useCostEstimate(
    unsupported && byBatchEstimate.data == null ? fullRunId : null,
  );
  const byRunActual = useCostActual(fullRunId);
  const sampleActual = useCostActual(sampleId);
  const estimate = byBatchEstimate.data ?? byRunEstimate.data ?? null;
  const runActuals = { sample: sampleActual.data ?? null, fullRun: byRunActual.data ?? null };
  // Supported: one actual with `runs[]` (the content split). Unsupported: the run reads, or the
  // batch answer itself while they load / when they have nothing.
  const actuals =
    unsupported && (runActuals.sample !== null || runActuals.fullRun !== null)
      ? runActuals
      : { sample: null, fullRun: batchActual };
  return { estimate, actuals };
}

function CostSectionBody({ data }: { data: BatchHeader }) {
  const { t } = useTranslation('content-enrichment');
  const { t: tw } = useTranslation('run-wizard');
  const subsidiaryId = useAuthStore((s) => s.user?.activeTenantId ?? '');
  const { estimate, actuals } = useBatchCost(data);
  const planned = data.plannedCost ?? null;
  const plannedUsd = planned?.totalUsd ?? null;
  const hasActual = actuals.sample !== null || actuals.fullRun !== null;
  const spent = spentUsd(actuals);
  const spentShown = spent !== null && spent > 0 ? spent : null;
  // Any recorded estimate has amounts (BELOW_THRESHOLD included).
  const estimatedUsd = estimate?.total?.avgUsd ?? null;
  // An older batch stored no plan: recompute it from today's rates, but only until the gate's
  // estimate or spend exists to say more.
  const recomputeOptions =
    plannedUsd === null && estimatedUsd === null && spentShown === null
      ? runCostOptionsOf(data)
      : null;
  const rates = useCostRates(
    subsidiaryId,
    rateConnectorOf(data),
    data.runConfig?.contentAttributes?.length ?? 0,
    recomputeOptions !== null,
  );
  const recomputed = recomputeOptions && rates.data ? runCost(rates.data, recomputeOptions) : null;
  const recomputedUsd = recomputed && recomputed.totalUsd > 0 ? recomputed.totalUsd : null;
  // The server's per-caller verdict wins (e.g. amounts hidden even for a lily.ai user).
  const showAmounts = useShowCostAmounts(estimate?.amountsHidden);
  if (!showAmounts) return null;

  const pieces: string[] = [];
  if (plannedUsd !== null)
    pieces.push(t('batchInfo.cost.planned', { amount: formatUsd(plannedUsd) }));
  else if (recomputedUsd !== null) {
    pieces.push(t('batchInfo.cost.recomputed', { amount: formatUsd(recomputedUsd) }));
  }
  const lead = pieces.length === 0;
  if (spentShown !== null && estimatedUsd !== null) {
    pieces.push(
      t(lead ? 'batchInfo.cost.spentOfLead' : 'batchInfo.cost.spentOfNext', {
        spent: formatUsd(spentShown),
        estimated: formatUsd(estimatedUsd),
      }),
    );
  } else if (spentShown !== null) {
    pieces.push(
      t(lead ? 'batchInfo.cost.spentLead' : 'batchInfo.cost.spentNext', {
        spent: formatUsd(spentShown),
      }),
    );
  } else if (estimatedUsd !== null) {
    pieces.push(t('batchInfo.cost.estimated', { estimated: formatUsd(estimatedUsd) }));
  }
  const summary = pieces.length > 0 ? pieces.join(' · ') : t('batchInfo.cost.none');

  const plannedParts = plannedByPart(planned?.lines ?? recomputed?.lines);
  const showPlannedColumn = Object.keys(plannedParts).length > 0;
  const products = runProducts(actuals);
  const fullRunLabel = (split: ContentSplit) =>
    split.fullRunReused
      ? t('batchInfo.cost.reused', { amount: formatUsd(0) })
      : split.fullRunUsd === null
        ? '—'
        : formatUsd(split.fullRunUsd);
  const rowClass = cn(
    'grid items-start gap-x-3 py-2 tabular-nums',
    showPlannedColumn
      ? 'grid-cols-[minmax(0,1fr)_72px_84px_84px]'
      : 'grid-cols-[minmax(0,1fr)_84px_84px]',
  );
  const perProduct = (amount: number | null) =>
    amount === null ? null : t('batchInfo.cost.perProductValue', { amount: formatRate(amount) });
  const totals: { key: string; label: string; total: string; per: string | null }[] = [
    ...(plannedUsd !== null
      ? [
          {
            key: 'planned',
            label: t('batchInfo.cost.plannedTotal'),
            total: formatUsd(plannedUsd),
            per: perProduct(planned?.perProductUsd ?? null),
          },
        ]
      : recomputedUsd !== null && recomputed
        ? [
            {
              key: 'recomputed',
              label: t('batchInfo.cost.recomputedTotal'),
              total: formatUsd(recomputedUsd),
              per: perProduct(recomputed.perProductUsd),
            },
          ]
        : []),
    ...(estimatedUsd !== null
      ? [
          {
            key: 'estimated',
            label: t('batchInfo.cost.estimateTotal'),
            total: `≈ ${formatUsd(estimatedUsd)}`,
            per: perProduct(estimate?.perProduct?.avgUsd ?? null),
          },
        ]
      : []),
    ...(hasActual && spent !== null
      ? [
          {
            key: 'spent',
            label: t('batchInfo.cost.spentTotal'),
            total: formatUsd(spent),
            per: perProduct(products > 0 ? spent / products : null),
          },
        ]
      : []),
  ];

  return (
    <CollapsibleSectionCard
      icon={CircleDollarSign}
      title={t('batchInfo.sections.cost')}
      summary={summary}
      testId={TEST_ID}
    >
      <div className="text-sm">
        <div
          data-testid={`${TEST_ID}-head`}
          className={cn(rowClass, 'text-muted-foreground pt-0 text-xs font-medium')}
        >
          <span>{t('batchInfo.cost.part')}</span>
          {showPlannedColumn && (
            <span className="text-right">
              {planned ? t('batchInfo.cost.plannedHead') : t('batchInfo.cost.atStartHead')}
            </span>
          )}
          <span className="inline-flex items-center justify-end gap-1">
            {tw('review.cost.head.perProduct')}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('batchInfo.cost.perProductInfo')}
                  data-testid={`${TEST_ID}-per-product-info`}
                  className="text-muted-foreground hover:text-foreground size-4 cursor-pointer p-0"
                >
                  <Info className="size-3.5" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[260px]">
                {t('batchInfo.cost.perProductInfo')}
              </TooltipContent>
            </Tooltip>
          </span>
          <span className="text-right">{t('batchInfo.cost.spentHead')}</span>
        </div>
        {costParts(actuals).map((p) => (
          <div
            key={p.key}
            data-testid={`${TEST_ID}-row-${p.key}`}
            className={cn(rowClass, 'border-t')}
          >
            <span className="min-w-0">
              {p.lineKey ? tw(`review.cost.lines.${p.lineKey}`) : t('batchInfo.cost.otherSteps')}
              {p.split && (
                <span
                  data-testid={`${TEST_ID}-row-${p.key}-split`}
                  className="text-muted-foreground block text-xs"
                >
                  {t('batchInfo.cost.contentSplit', {
                    sample: p.split.sampleUsd === null ? '—' : formatUsd(p.split.sampleUsd),
                    fullRun: fullRunLabel(p.split),
                  })}
                </span>
              )}
            </span>
            {showPlannedColumn && (
              <span
                data-testid={`${TEST_ID}-row-${p.key}-planned`}
                className="text-muted-foreground text-right"
              >
                {plannedParts[p.key] === undefined ? '—' : formatUsd(plannedParts[p.key] ?? 0)}
              </span>
            )}
            <span
              data-testid={`${TEST_ID}-row-${p.key}-per-product`}
              className="text-muted-foreground text-right"
            >
              {p.perProductUsd === null ? '—' : formatRate(p.perProductUsd)}
            </span>
            <span data-testid={`${TEST_ID}-row-${p.key}-total`} className="text-right font-medium">
              {p.totalUsd === null ? '—' : formatUsd(p.totalUsd)}
            </span>
          </div>
        ))}
        {totals.length > 0 && (
          <dl data-testid={`${TEST_ID}-totals`} className="mt-1 border-t-2 pt-1">
            {totals.map((row) => (
              <div
                key={row.key}
                data-testid={`${TEST_ID}-${row.key}-row`}
                className="flex items-baseline justify-between gap-3 py-1.5 tabular-nums"
              >
                <dt className="font-semibold">{row.label}</dt>
                <dd className="text-right">
                  <span data-testid={`${TEST_ID}-${row.key}-total`} className="font-semibold">
                    {row.total}
                  </span>
                  {row.per && <span className="text-muted-foreground ml-2 text-xs">{row.per}</span>}
                </dd>
              </div>
            ))}
          </dl>
        )}
        {hasActual && (
          <p className="text-muted-foreground pt-2 text-xs">{t('batchInfo.cost.partsNote')}</p>
        )}
      </div>
    </CollapsibleSectionCard>
  );
}
